import { Gem, Globe, Lock, Scale, ShieldCheck, Award } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const ShapeGlyph = ({ d, rounded }) => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="#C9A24B" strokeWidth="1.2">
    {rounded ? <rect x="4" y="4" width="16" height="16" rx="6" /> : <path d={d} />}
  </svg>
);

const SHAPES = [
  { name: "Round", d: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z" },
  { name: "Oval", d: "M12 3c4 0 7 4 7 9s-3 9-7 9-7-4-7-9 3-9 7-9Z" },
  { name: "Emerald", d: "M6 4h12l2 2v12l-2 2H6l-2-2V6l2-2Z" },
  { name: "Princess", d: "M5 5h14v14H5z" },
  { name: "Cushion", rounded: true },
  { name: "Pear", d: "M12 3c3 3 6 7 6 11a6 6 0 1 1-12 0c0-4 3-8 6-11Z" },
  { name: "Radiant", d: "M7 4h10l3 3v10l-3 3H7l-3-3V7l3-3Z" },
  { name: "Marquise", d: "M12 2c4 4 4 16 0 20-4-4-4-16 0-20Z" },
];

const CARD =
  "glass-panel rounded-2xl p-8 transition-all duration-500 hover:border-[#C9A24B]/40 lg:p-10";

export const CapabilitiesBento = () => (
  <section id="capabilities" data-testid="capabilities-bento" className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-32">
    <Reveal>
      <p className="eyebrow mb-4">Capabilities</p>
      <h2 className="font-display max-w-2xl text-3xl font-light leading-snug tracking-tight text-white sm:text-4xl lg:text-5xl">
        Every shape. Every brief. <span className="gold-text italic">One desk.</span>
      </h2>
    </Reveal>

    <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      <Reveal className="md:col-span-2" data-testid="cap-card-shapes">
        <div className={`${CARD} h-full`}>
          <Gem className="mb-5 h-6 w-6 text-[#C9A24B]" strokeWidth={1.5} />
          <h3 className="font-display text-2xl text-white">Eight signature shapes</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#B8BCC2]">
            From round brilliants to fancy cuts — calibrated, matched pairs, and layouts on request.
          </p>
          <div className="mt-7 grid grid-cols-4 gap-3 sm:grid-cols-8">
            {SHAPES.map((s) => (
              <div
                key={s.name}
                data-testid={`cap-shape-${s.name.toLowerCase()}`}
                className="group flex flex-col items-center gap-2 rounded-xl border border-white/10 py-3 transition-colors duration-300 hover:border-[#C9A24B]/50"
              >
                <ShapeGlyph d={s.d} rounded={s.rounded} />
                <span className="text-[10px] uppercase tracking-wider text-[#717680] transition-colors group-hover:text-[#E8DCC0]">
                  {s.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className={`${CARD} flex h-full flex-col`} data-testid="cap-card-carat">
          <Scale className="mb-5 h-6 w-6 text-[#C9A24B]" strokeWidth={1.5} />
          <h3 className="font-display text-2xl text-white">Carat range</h3>
          <p className="font-display mt-6 text-5xl font-light gold-text">0.30 — 15.00+</p>
          <p className="mt-4 text-sm leading-relaxed text-[#B8BCC2]">
            Melee and calibrated goods through to certified statement stones, natural and lab-grown.
          </p>
        </div>
      </Reveal>

      <Reveal>
        <div className={`${CARD} h-full`} data-testid="cap-card-certification">
          <ShieldCheck className="mb-5 h-6 w-6 text-[#C9A24B]" strokeWidth={1.5} />
          <h3 className="font-display text-2xl text-white">Certified sourcing</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#B8BCC2]">
            GIA, IGI, and HRD Antwerp papers verified before offer. Laser inscription and report-number
            matching on every certified stone.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className={`${CARD} h-full`} data-testid="cap-card-bespoke">
          <Award className="mb-5 h-6 w-6 text-[#C9A24B]" strokeWidth={1.5} />
          <h3 className="font-display text-2xl text-white">Bespoke sourcing</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#B8BCC2]">
            Send the brief — 4Cs, budget position, timeline. Our desk searches the network and returns
            options within 48 hours.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className={`${CARD} h-full`} data-testid="cap-card-logistics">
          <Lock className="mb-5 h-6 w-6 text-[#C9A24B]" strokeWidth={1.5} />
          <h3 className="font-display text-2xl text-white">Secure logistics</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#B8BCC2]">
            Fully insured door-to-door carriage and JLT vault holding for memo and call-off stock.
          </p>
          <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#717680]">
            <Globe className="h-3.5 w-3.5" /> Dubai · Mumbai · Antwerp · Hong Kong
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
